import React, { useState, useEffect } from 'react';
import {
  RevenueTrendsChart,
  QuoteConversionChart,
  SupplierPerformanceChart,
  PerformanceOverviewChart,
  CommissionStatusChart
} from './AnalyticsCharts';
import { analyticsService } from '../../services';
// AnalyticsCharts.jsx (react-chartjs-2 canvases) relies on the parent
// providing a sized wrapper via these classes - without them the Chart.js
// canvases collapse to zero height. Kept even though the rest of the page
// is Tailwind/CSS-var now.
import './AnalyticsCharts.css';

// Voyager Pro reskin (Figma AnalyticsPage) - reskinned the page frame/cards
// to Tailwind/CSS-var; deliberately left AnalyticsCharts.jsx (canvas/SVG
// chart rendering) untouched, same scope-boundary precedent as Quote
// Builder / EditItineraryModal - rewriting chart-drawing code carries much
// more regression risk than visual reward here. Independent's
// IndependentAnalytics.jsx is a genuinely separate, differently-shaped
// implementation (computes its own metrics client-side from unified
// quotes/itineraries/clients data, not the same dashboardData shape) -
// reskinned separately rather than merged, to avoid a risky data-model
// unification this late in the pass.

const periodOptions = [
  { value: '1', label: 'Today' },
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 3 Months' },
  { value: '365', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

function MetricCard({ title, children, wide }) {
  return (
    <div className={`bg-white rounded-2xl border border-[var(--border)] p-5 ${wide ? 'md:col-span-2' : ''}`}>
      <h2 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-widest mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Metric({ value, label, highlight }) {
  return (
    <div>
      <div className={`text-2xl font-bold ${highlight ? '' : 'text-[var(--foreground)]'}`} style={highlight ? { color: 'var(--accent)' } : {}}>{value}</div>
      <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{label}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
      <h3 className="text-sm font-bold text-[var(--foreground)]">{title}</h3>
      <p className="text-xs text-[var(--muted-foreground)] mb-3">{subtitle}</p>
      {children}
    </div>
  );
}

const AgentAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDates, setShowCustomDates] = useState(false);

  const [dashboardData, setDashboardData] = useState(null);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [supplierPerformance, setSupplierPerformance] = useState([]);
  const [quoteConversion, setQuoteConversion] = useState(null);
  const [clientDemographics, setClientDemographics] = useState(null);
  const [error, setError] = useState(null);

  const [showCharts, setShowCharts] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = period === 'custom' ? { start: customDateRange.start, end: customDateRange.end } : { period };
      const [dashboard, revenue, supplier, conversion, demographics] = await Promise.all([
        analyticsService.getDashboard(params),
        analyticsService.getRevenueTrends(params),
        analyticsService.getSupplierPerformance(params),
        analyticsService.getQuoteConversion(params),
        analyticsService.getClientDemographics(params),
      ]);
      setDashboardData(dashboard);
      setRevenueTrends(revenue);
      setSupplierPerformance(supplier);
      setQuoteConversion(conversion);
      setClientDemographics(demographics);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshAnalytics = async () => {
    setRefreshing(true);
    try {
      await analyticsService.clearCache();
      await fetchAnalytics();
    } catch (err) {
      console.error('Error refreshing analytics:', err);
      setError('Failed to refresh analytics data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (period !== 'custom' || (customDateRange.start && customDateRange.end)) {
      fetchAnalytics();
    }
    // eslint-disable-next-line
  }, [period, customDateRange]);

  const handlePeriodChange = (e) => {
    const value = e.target.value;
    setPeriod(value);
    setShowCustomDates(value === 'custom');
  };

  const getConversionRate = () => {
    if (!quoteConversion || !quoteConversion.total_quotes) return '0';
    return ((quoteConversion.converted_quotes / quoteConversion.total_quotes) * 100).toFixed(1);
  };

  if (loading) {
    return <div className="voyager-app flex items-center justify-center min-h-[400px] text-sm text-[var(--muted-foreground)]">Loading analytics…</div>;
  }

  if (error) {
    return (
      <div className="voyager-app p-6">
        <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-4 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchAnalytics} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="voyager-app p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">My Analytics</h1>
            <p className="text-[var(--muted-foreground)] text-sm">Track your business performance and metrics.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={period} onChange={handlePeriodChange} className="px-4 py-2.5 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer">
              {periodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {showCustomDates && (
              <div className="flex items-center gap-2">
                <input type="date" value={customDateRange.start} onChange={e => setCustomDateRange({ ...customDateRange, start: e.target.value })} className="px-3 py-2.5 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none" />
                <span className="text-[var(--muted-foreground)] text-sm">to</span>
                <input type="date" value={customDateRange.end} onChange={e => setCustomDateRange({ ...customDateRange, end: e.target.value })} className="px-3 py-2.5 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none" />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="inline-flex rounded-xl border border-[var(--border)] overflow-hidden">
            <button onClick={() => setShowCharts(true)} className={`px-4 py-2 text-sm font-semibold transition-colors ${showCharts ? 'text-white' : 'text-[var(--foreground)] bg-white hover:bg-[var(--muted)]'}`} style={showCharts ? { backgroundColor: 'var(--primary)' } : {}}>📊 Charts</button>
            <button onClick={() => setShowCharts(false)} className={`px-4 py-2 text-sm font-semibold transition-colors ${!showCharts ? 'text-white' : 'text-[var(--foreground)] bg-white hover:bg-[var(--muted)]'}`} style={!showCharts ? { backgroundColor: 'var(--primary)' } : {}}>📋 Tables</button>
          </div>
          <button onClick={refreshAnalytics} disabled={refreshing} className="px-4 py-2 rounded-xl border border-[var(--border)] bg-white text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors disabled:opacity-50">
            {refreshing ? '🔄 Refreshing…' : '↻ Refresh Data'}
          </button>
        </div>

        {showCharts && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChartCard title="📊 Performance Overview" subtitle="Quote activity breakdown"><div className="performance-chart-container"><PerformanceOverviewChart data={dashboardData} /></div></ChartCard>
            <ChartCard title="💰 Revenue Trends" subtitle="Commission earnings over time"><div className="revenue-chart-container"><RevenueTrendsChart data={revenueTrends} /></div></ChartCard>
            <ChartCard title="🎯 Quote Conversion" subtitle="Success rate analysis"><div className="conversion-chart-container"><QuoteConversionChart data={quoteConversion} /></div></ChartCard>
            <ChartCard title="✅ Commission Status" subtitle="Approval status breakdown"><div className="commission-chart-container"><CommissionStatusChart data={dashboardData} /></div></ChartCard>
            <div className="md:col-span-2"><ChartCard title="🏢 Top Suppliers" subtitle="Performance by commission earned"><div className="supplier-chart-container"><SupplierPerformanceChart data={supplierPerformance} /></div></ChartCard></div>
          </div>
        )}

        {!showCharts && (
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">📋 Detailed Analytics Tables</h2>
            <p className="text-sm text-[var(--muted-foreground)]">Comprehensive data breakdown and metrics.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard title="Performance Overview">
            <div className="grid grid-cols-2 gap-4">
              <Metric value={dashboardData?.quotes?.total_quotes || 0} label="Total Quotes" />
              <Metric value={dashboardData?.quotes?.sent_quotes || 0} label="Sent Quotes" />
              <Metric value={dashboardData?.quotes?.accepted_quotes || 0} label="Accepted Quotes" />
              <Metric value={formatCurrency(dashboardData?.quotes?.total_quote_value)} label="Total Quote Value" />
            </div>
          </MetricCard>

          <MetricCard title="Commission Summary">
            <div className="grid grid-cols-2 gap-4">
              <Metric value={dashboardData?.commissions?.total_commissions || 0} label="Total Commissions" />
              <Metric value={dashboardData?.commissions?.approved_commissions || 0} label="Approved" />
              <Metric value={formatCurrency(dashboardData?.commissions?.total_earnings)} label="Total Earnings" highlight />
              <Metric value={formatCurrency(dashboardData?.commissions?.pending_earnings)} label="Pending Earnings" />
            </div>
          </MetricCard>

          <MetricCard title="Client Demographics">
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><span className="text-[var(--muted-foreground)]">Total Clients</span><span className="font-bold text-[var(--foreground)]">{clientDemographics?.total_clients || 0}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[var(--muted-foreground)]">US Clients</span><span className="font-bold text-[var(--foreground)]">{clientDemographics?.us_clients || 0}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[var(--muted-foreground)]">International Clients</span><span className="font-bold text-[var(--foreground)]">{clientDemographics?.international_clients || 0}</span></div>
            </div>
          </MetricCard>

          <MetricCard title="Quote Conversion">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-3xl font-bold" style={{ color: 'var(--accent)' }}>{getConversionRate()}%</div>
                <div className="text-xs text-[var(--muted-foreground)]">Conversion Rate</div>
              </div>
              <div className="space-y-1.5 text-sm flex-1">
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Total Quotes</span><span className="font-semibold text-[var(--foreground)]">{quoteConversion?.total_quotes || 0}</span></div>
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Converted</span><span className="font-semibold text-[var(--foreground)]">{quoteConversion?.converted_quotes || 0}</span></div>
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Avg Days to Convert</span><span className="font-semibold text-[var(--foreground)]">{Math.round(quoteConversion?.avg_conversion_days || 0)}</span></div>
              </div>
            </div>
          </MetricCard>

          <MetricCard title="Revenue Trends" wide>
            {revenueTrends.length > 0 ? (
              <div className="divide-y divide-[var(--border)]">
                <div className="grid grid-cols-3 text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider pb-2"><span>Month</span><span>Revenue</span><span>Commissions</span></div>
                {revenueTrends.map((trend, i) => (
                  <div key={i} className="grid grid-cols-3 text-sm py-2 text-[var(--foreground)]">
                    <span>{trend.month}</span><span className="font-semibold">{formatCurrency(trend.revenue)}</span><span>{trend.commission_count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[var(--muted-foreground)]">No revenue data available for this period.</p>}
          </MetricCard>

          <MetricCard title="Top Suppliers" wide>
            {supplierPerformance.length > 0 ? (
              <div className="divide-y divide-[var(--border)]">
                <div className="grid grid-cols-4 text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider pb-2"><span>Supplier</span><span>Bookings</span><span>Total Commission</span><span>Avg Commission</span></div>
                {supplierPerformance.map((s, i) => (
                  <div key={i} className="grid grid-cols-4 text-sm py-2 text-[var(--foreground)]">
                    <span>{s.supplier || 'Unknown'}</span><span>{s.booking_count}</span><span className="font-semibold">{formatCurrency(s.total_commission)}</span><span>{formatCurrency(s.avg_commission)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[var(--muted-foreground)]">No supplier data available for this period.</p>}
          </MetricCard>

          <MetricCard title="Travel Status">
            <div className="grid grid-cols-2 gap-4">
              <Metric value={dashboardData?.itineraries?.total_itineraries || 0} label="Total Itineraries" />
              <Metric value={dashboardData?.itineraries?.upcoming_travel || 0} label="Upcoming" />
              <Metric value={dashboardData?.itineraries?.active_travel || 0} label="Active" />
              <Metric value={dashboardData?.itineraries?.completed_travel || 0} label="Completed" />
            </div>
          </MetricCard>

          <MetricCard title="Client Activity">
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><span className="text-[var(--muted-foreground)]">New Clients in Period</span><span className="font-bold text-[var(--foreground)]">{dashboardData?.clients?.total_clients || 0}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[var(--muted-foreground)]">Total Active Clients</span><span className="font-bold text-[var(--foreground)]">{clientDemographics?.total_clients || 0}</span></div>
            </div>
          </MetricCard>
        </div>
      </div>
    </div>
  );
};

export default AgentAnalytics;
